import type { ReactNode } from "react";
import {
  Building2,
  Check,
  ChevronsUpDown,
  FileCode2,
  Inbox,
  SearchX,
  Send,
  ShoppingBag,
} from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import { Container, CtaLink, Glow, MockFrame, Section, SectionHeading } from "./primitives";

/**
 * The band for accountants: what an accountant gets when their clients use
 * Zettl, and the way to /pro-ucetni. Every line is backed by what ships
 * (docs on the guide page, src/lib/guides/content.ts → guideAccountants):
 * the company switcher (WorkspaceSwitcher), the Chybějící doklady badge
 * (AppSidebar), the Monday e-mail (weekly-missing-documents), POHODA
 * (export ZIP, pohoda-send via mServer), ISDOC and Shoptet. Company names
 * and numbers in the mockup are invented.
 */
export function AccountantsSection({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  const points: { icon: ReactNode; title: string; text: string }[] = [
    {
      icon: <Inbox className="size-4" />,
      title: tr("Doklady chodí samy", "Documents arrive on their own"),
      text: tr(
        "Klient je přeposílá na adresu své firmy, Zettl je páruje s platbami z Fio.",
        "The client forwards them to their company address; Zettl matches them with Fio payments."
      ),
    },
    {
      icon: <SearchX className="size-4" />,
      title: tr("Vidíte, co chybí", "You see what's missing"),
      text: tr(
        "Stránka Chybějící doklady a v pondělí e-mail vlastníkovi a správcům.",
        "The Missing documents page, and a Monday e-mail to owners and admins."
      ),
    },
    {
      icon: <Send className="size-4" />,
      title: tr("Do POHODY jedním krokem", "Into POHODA in one step"),
      text: tr(
        "Měsíc jako ZIP s XML, nebo přímo přes mServer, když je dostupný z internetu.",
        "A month as a ZIP of XML, or straight in through mServer when it is reachable online."
      ),
    },
    {
      icon: <FileCode2 className="size-4" />,
      title: tr("ISDOC a e-shopy", "ISDOC and e-shops"),
      text: tr(
        "Faktury v ISDOC tam i zpět, ze Shoptetu automaticky nebo z exportu.",
        "Invoices as ISDOC both ways; from Shoptet automatically or from an export."
      ),
    },
  ];

  const clients = [
    { name: "Studio Lípa s.r.o.", current: true },
    { name: "Kavárna Na Rohu s.r.o.", current: false },
    { name: "Jana Dvořáková", current: false },
  ];

  return (
    <Section id="pro-ucetni" labelledBy="accountants-title" contours="flank">
      <Container>
        <div className="relative isolate overflow-hidden rounded-3xl border bg-background/80 p-6 shadow-[0_1px_2px_rgb(0_0_0/0.04),0_24px_48px_-24px_rgb(0_0_0/0.18)] dark:bg-background/60 dark:shadow-[0_1px_0_rgb(255_255_255/0.04)_inset,0_24px_48px_-24px_rgb(0_0_0/0.7)] sm:p-10 lg:p-12">
          <Glow at="top" className="-z-10" />
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-14">
            <div>
              <SectionHeading
                id="accountants-title"
                eyebrow={tr("Pro účetní", "For accountants")}
                title={tr(
                  "Doklady a platby z Fio všech klientů na jednom místě",
                  "All your clients' documents and Fio payments in one place"
                )}
                lead={tr(
                  "Klienti posílají doklady, Zettl je páruje s platbami z jejich účtu u Fio a vy dostanete hotový měsíc, i rovnou do POHODY.",
                  "Clients send documents, Zettl matches them with the payments on their Fio account, and you get a finished month, straight into POHODA if you like."
                )}
              />
              <ul className="mt-8 grid gap-5 sm:grid-cols-2">
                {points.map((p) => (
                  <li key={p.title} className="flex gap-3.5">
                    <span
                      aria-hidden="true"
                      className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-[hsl(var(--action))] dark:text-primary"
                    >
                      {p.icon}
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold">{p.title}</h3>
                      <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                        {p.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <CtaLink to={localePath(locale, "/for-accountants")} arrow>
                  {tr("Zettl pro účetní", "Zettl for accountants")}
                </CtaLink>
                <CtaLink to={localePath(locale, "/zettl-and-pohoda")} variant="outline">
                  {tr("Jak dostat data do POHODY", "Getting data into POHODA")}
                </CtaLink>
              </div>
            </div>

            <div className="relative">
              <MockFrame
                label={tr(
                  "Ukázka: přepínání mezi firmami klientů, počet plateb bez dokladu a výsledek odeslání měsíce do POHODY. Vymyšlená data.",
                  "Sample: switching between clients' companies, the count of payments without a document, and the result of sending a month to POHODA. Invented data."
                )}
                className="bg-card"
              >
                <div className="border-b p-4">
                  <div className="flex items-center justify-between rounded-lg border bg-background/60 px-3 py-2 text-[13px]">
                    <span className="flex items-center gap-2 font-medium">
                      <Building2 className="size-4 text-muted-foreground" />
                      Studio Lípa s.r.o.
                    </span>
                    <ChevronsUpDown className="size-4 text-muted-foreground" />
                  </div>
                  <ul className="mt-2 divide-y rounded-lg border text-[13px]">
                    {clients.map((c) => (
                      <li
                        key={c.name}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                      >
                        <span className={c.current ? "font-medium" : "text-muted-foreground"}>
                          {c.name}
                        </span>
                        {c.current && (
                          <Check className="size-4 text-[hsl(var(--action))] dark:text-primary" />
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-secondary/60 px-3 py-2 text-[13px]">
                    <span>{tr("Chybějící doklady", "Missing documents")}</span>
                    <span className="rounded-full bg-[hsl(var(--dot-amber))]/20 px-2 py-0.5 text-xs font-semibold tabular-nums">
                      3
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[13px] font-semibold">
                      {tr("Odeslat do POHODY · říjen", "Send to POHODA · October")}
                    </p>
                    <DotChip color="green">{tr("Hotovo", "Done")}</DotChip>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    {[
                      { n: "41", label: tr("naimportováno", "imported"), color: "green" as const },
                      { n: "3", label: tr("už v POHODĚ", "already there"), color: "blue" as const },
                      { n: "0", label: tr("chyba", "errors"), color: "muted" as const },
                    ].map((s) => (
                      <div key={s.label} className="rounded-lg border bg-background/50 px-2 py-2.5">
                        <p className="text-lg font-semibold tabular-nums">{s.n}</p>
                        <p className="mt-0.5 flex items-start justify-center gap-1 text-[11px] leading-tight text-muted-foreground">
                          <span
                            aria-hidden="true"
                            className="mt-[3px] size-1.5 shrink-0 rounded-full"
                            style={{
                              backgroundColor:
                                s.color === "muted"
                                  ? "hsl(var(--muted-foreground))"
                                  : `hsl(var(--dot-${s.color}))`,
                            }}
                          />
                          <span>{s.label}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {[
                      { icon: <FileCode2 className="size-3" />, label: "ISDOC" },
                      { icon: <ShoppingBag className="size-3" />, label: "Shoptet" },
                      { icon: <Send className="size-3" />, label: "POHODA mServer" },
                    ].map((x) => (
                      <span
                        key={x.label}
                        className="inline-flex items-center gap-1 rounded-md border bg-background/60 px-2 py-1 text-xs font-medium"
                      >
                        <span className="text-muted-foreground">{x.icon}</span>
                        {x.label}
                      </span>
                    ))}
                  </div>
                </div>
              </MockFrame>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
